import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, resolveImage } from "@/lib/api";
import { ArrowLeft, CaretLeft, CaretRight, X } from "@phosphor-icons/react";

export default function ComicReader() {
  const { id } = useParams();
  const [comic, setComic] = useState(null);
  const [err, setErr] = useState(false);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    api.get(`/comics/${id}`).then((r) => setComic(r.data)).catch(() => setErr(true));
  }, [id]);

  const pages = comic?.pages || [];
  const total = pages.length;

  const next = useCallback(() => setIdx((i) => Math.min(total - 1, i + 1)), [total]);
  const prev = useCallback(() => setIdx((i) => Math.max(0, i - 1)), []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, prev]);

  if (err) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="reader-not-found">
        <h1 className="font-display text-4xl uppercase">Comic Not Found</h1>
        <Link to="/comics" className="btn-red inline-block mt-6">Back to Comics</Link>
      </div>
    );
  }
  if (!comic) return <div className="max-w-3xl mx-auto px-6 py-24" data-testid="reader-loading">Loading...</div>;

  if (total === 0) {
    return (
      <div className="bg-black min-h-screen" data-testid="reader-locked">
        <div className="max-w-3xl mx-auto px-6 py-16 text-center">
          <Link to="/comics" className="inline-flex items-center gap-2 text-dim hover:text-[#FF3B30] text-xs uppercase tracking-widest" data-testid="reader-back">
            <ArrowLeft size={14} /> Back to Comics
          </Link>
          {comic.cover_image && (
            <div className="relative inline-block mt-10 group">
              <div className="absolute -inset-3 bg-gradient-to-r from-[#FF3B30] via-[#FFD700] to-[#FF3B30] opacity-25 blur-2xl group-hover:opacity-40 transition-opacity" />
              <img
                src={resolveImage(comic.cover_image)}
                alt={`${comic.title} ${comic.issue} cover`}
                className="relative w-[280px] sm:w-[360px] h-auto shadow-2xl shadow-red-900/40 border border-white/10"
                data-testid="reader-locked-cover"
              />
            </div>
          )}
          <div className="section-eyebrow justify-center mt-10 text-[#FFD700]">Locked · Members Only</div>
          <h1 className="font-display text-4xl sm:text-5xl uppercase mt-3" data-testid="reader-locked-title">
            The First <span className="text-[#FF3B30]">3 Pages</span> Are <span className="text-[#FFD700]">Free</span>
          </h1>
          <p className="text-white/75 mt-4 max-w-lg mx-auto">
            Drop your email and we&apos;ll send a 3-page preview PDF + the Helmet of Michael phone wallpaper instantly.
          </p>
          <Link to="/#free-preview-section" className="btn-red mt-7 inline-flex items-center gap-2" data-testid="reader-locked-cta">
            Get My 3 Free Pages
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-black min-h-screen" data-testid="comic-reader-page">
      {/* Reader header */}
      <div className="sticky top-0 z-30 bg-black/90 backdrop-blur border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between gap-4">
          <Link to="/comics" className="inline-flex items-center gap-2 text-dim hover:text-[#FF3B30] text-xs uppercase tracking-widest" data-testid="reader-back">
            <X size={16} /> Close
          </Link>
          <div className="font-display tracking-widest uppercase text-sm truncate" data-testid="reader-title">
            {comic.title} <span className="text-[#FF3B30]">{comic.issue}</span>
          </div>
          <div className="font-display tracking-widest uppercase text-xs text-dim" data-testid="reader-page-indicator">
            Page {idx + 1} / {total}
          </div>
        </div>
      </div>

      {/* Page viewer */}
      <div className="relative max-w-5xl mx-auto px-4 py-6" data-testid="reader-viewport">
        <div className="aspect-[3/4] bg-[#0A0A0A] border border-white/10 flex items-center justify-center overflow-hidden relative">
          <img
            src={resolveImage(pages[idx])}
            alt={`Page ${idx + 1}`}
            className="w-full h-full object-contain"
            data-testid={`reader-page-${idx + 1}`}
          />
          {/* Tap zones */}
          <button onClick={prev} disabled={idx === 0}
                  className="absolute inset-y-0 left-0 w-1/3 group disabled:opacity-30"
                  data-testid="reader-tap-prev" aria-label="previous page">
            <span className="hidden sm:flex absolute left-3 top-1/2 -translate-y-1/2 w-12 h-12 items-center justify-center bg-black/60 group-hover:bg-[#FF3B30] transition-colors">
              <CaretLeft size={24} weight="bold" />
            </span>
          </button>
          <button onClick={next} disabled={idx === total - 1}
                  className="absolute inset-y-0 right-0 w-1/3 group disabled:opacity-30"
                  data-testid="reader-tap-next" aria-label="next page">
            <span className="hidden sm:flex absolute right-3 top-1/2 -translate-y-1/2 w-12 h-12 items-center justify-center bg-black/60 group-hover:bg-[#FF3B30] transition-colors">
              <CaretRight size={24} weight="bold" />
            </span>
          </button>
        </div>

        {/* Bottom controls */}
        <div className="mt-4 flex items-center justify-between gap-3">
          <button onClick={prev} disabled={idx === 0} className="btn-ghost inline-flex items-center gap-2 disabled:opacity-30" data-testid="reader-prev-btn">
            <CaretLeft size={16} weight="bold" /> Prev
          </button>

          {/* Page dots */}
          <div className="flex gap-2 overflow-x-auto" data-testid="reader-dots">
            {pages.map((p, i) => (
              <button key={p || `page-${i}`} onClick={() => setIdx(i)} aria-label={`Go to page ${i + 1}`}
                      data-testid={`reader-dot-${i + 1}`}
                      className={`w-3 h-3 rounded-full transition-colors ${i === idx ? "bg-[#FF3B30]" : "bg-white/20 hover:bg-white/40"}`} />
            ))}
          </div>

          <button onClick={next} disabled={idx === total - 1} className="btn-red inline-flex items-center gap-2 disabled:opacity-30" data-testid="reader-next-btn">
            Next <CaretRight size={16} weight="bold" />
          </button>
        </div>

        <div className="mt-8 text-center">
          <p className="text-dim text-sm">Liked the preview? Pre-order the full issue.</p>
          <Link to="/comics" className="btn-red inline-block mt-3" data-testid="reader-buy-cta">Pre-Order {comic.issue}</Link>
        </div>
      </div>
    </div>
  );
}
